import { Box, Typography, Card, Divider, useTheme } from '@mui/material';
import {
  MessageOutlined as MessageIcon,
  VisibilityOutlined as VisibilityIcon,
  ShieldOutlined as ShieldIcon,
} from '@mui/icons-material';

interface CharacterDetailsProps {
  character: any;
}

export function CharacterSection({ character }: CharacterDetailsProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Card
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          color: '#fff',
          p: 3,
          borderRadius: 3,
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)',
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#fff' }}>
          Greeting
        </Typography>
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
          {character.first_message}
        </Typography>
        <Divider sx={{ my: 3, backgroundColor: 'rgba(255,255,255,0.08)' }} />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#fff' }}>
          Description
        </Typography>
        <Typography variant="body1" paragraph sx={{ lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
          {character.description}
        </Typography>
        <Divider sx={{ my: 3, backgroundColor: 'rgba(255,255,255,0.08)' }} />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#fff' }}>
          Character Details
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Box flex={1} display="flex" alignItems="center">
            <MessageIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.6)' }} />
            <Typography variant="body2" color="rgba(255,255,255,0.6)">
              <strong style={{ color: '#fff' }}>Interactions:</strong> {character.chat_messages_count || 0}
            </Typography>
          </Box>
          <Box flex={1} display="flex" alignItems="center">
            <VisibilityIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.6)' }} />
            <Typography variant="body2" color="rgba(255,255,255,0.6)">
              <strong style={{ color: '#fff' }}>Visibility:</strong> {character.is_public ? 'Public' : 'Private'}
            </Typography>
          </Box>
          <Box flex={1} display="flex" alignItems="center">
            <ShieldIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.6)' }} />
            <Typography variant="body2" color="rgba(255,255,255,0.6)">
              <strong style={{ color: '#fff' }}>Content Rating:</strong> {character.is_nsfw ? 'NSFW' : 'SFW'}
            </Typography>
          </Box>
        </Box>
      </Card>

      <Card
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          color: '#fff',
          p: 3,
          borderRadius: 3,
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)',
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#fff' }}>
          Gallery
        </Typography>
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: 2,
            py: 1,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 3 },
          }}
        >
          {character.gallery?.map((img: string, idx: number) => (
            <Box
              key={idx}
              component="img"
              src={img}
              alt={`Gallery ${idx + 1}`}
              sx={{
                height: 120,
                width: 160,
                objectFit: 'cover',
                borderRadius: 1,
                flex: '0 0 auto',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                border: '1px solid rgba(255,255,255,0.08)',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
          ))}
        </Box>
      </Card>

      <Card
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          color: '#fff',
          p: 3,
          borderRadius: 3,
          boxShadow: '0 20px 40px -10px rgba(0,0,0,0.3)',
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#fff' }}>
          Notes
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
          {character.notes || 'No notes available.'}
        </Typography>
      </Card>
    </Box>
  );
}